import { Map } from 'immutable';

import { getCombinationsErrors, getOptionsErrors, getVariantNameError, getVariantsErrors } from '../validations/variations';
import { findAnalogueScale, getAvailable } from '../taxonomy';
import { updateCombinations } from './combinations';
import { updateInfluence } from './influence';
import { setInvalid } from './errors';
import { getSize } from '../iterable/getSize';
import { get } from '../iterable/get';

import { ALL, COMBINATIONS, DELETED, ERRORS, INFLUENCE, VARIANTS } from '../../constants/variations';
import { CUSTOM_PROPERTY_IDS } from '../../constants/taxonomy';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { ERROR } from '../../constants/product';
import { EMPTY } from '../../constants';
import ATTRUBUTE from '../../constants/attributes';

export function updateVariations({
  category,
  channel,
  inventory,
  validateCombinations = false,
  validateOptions = false,
  variants,
  variations: source,
}) {
  let variations = source;
  const combinations = updateCombinations({ channel, inventory, variants, variations });

  variations = variations.set(COMBINATIONS, combinations).set(VARIANTS, variants);
  variations = variations.set(ERROR, getVariantsErrors({ category, channel, variations }));

  if (validateOptions) {
    variations = variations.set(ERROR, getOptionsErrors({ channel, variations }));
  }

  if (validateCombinations) {
    variations = variations.set(ERROR, getCombinationsErrors({ channel, variations }));
  }

  return setInvalid(updateInfluence({ channel, variations }));
}

export function addVariant({ category, channel, inventory, variant: source, variations }) {
  let variant = source.set('options', EMPTY.LIST);
  let variants = variations.get(VARIANTS);
  const variantIndex = getSize(variants);
  let errors = variations
    .get(ERROR, EMPTY.MAP)
    .setIn([VARIANTS, variantIndex], Map({ options: ERRORS.OPTIONS.NO_OPTIONS }));

  const previousId = variants.getIn([variantIndex - 1, 'id']);
  variant = variant.set('id', previousId < 0 ? previousId - 1 : -1);

  switch (channel) {
    case ETSY: {
      const property = variant.get('property');
      const { availableScales } = getAvailable({ category, channel, property });

      if (getSize(availableScales)) {
        variant = variant.set('scale', 0);
        errors = errors.setIn([VARIANTS, variantIndex, 'scale'], true);
      }

      break;
    }

    default: {
      break;
    }
  }

  variants = variants.push(variant);

  return updateVariations({
    category,
    channel,
    inventory,
    variants,
    variations: variations.set(ERROR, errors),
  });
}

export function deleteVariant({ category, channel, inventory, variantIndex, variations }) {
  function reduceErrors(result, value, key) {
    return key < variantIndex
      ? result.set(key, value)
      : key > variantIndex
        ? result.set(key - 1, value)
        : result;
  }

  function updatePropertyId(leftVariant, leftVariantIndex) {
    return CUSTOM_PROPERTY_IDS[channel].includes(leftVariant.get('property'))
      ? leftVariant.set('property', CUSTOM_PROPERTY_IDS[channel][leftVariantIndex])
      : leftVariant;
  }

  function clearInfluence(influence = EMPTY.MAP) {
    return influence.delete(variantIndex);
  }

  let errors = variations.getIn([ERRORS, VARIANTS], EMPTY.MAP).reduce(reduceErrors, EMPTY.MAP);
  let variants = variations.get(VARIANTS).splice(variantIndex, 1);
  let influence = variations.get(INFLUENCE);

  function updateNameErrors(result, leftVariant, leftVariantIndex) {
    const error = getVariantNameError({
      channel,
      name: leftVariant.get('name'),
      variants,
      variantIndex: leftVariantIndex,
    });

    return error
      ? result.setIn([leftVariantIndex, 'name'], error)
      : result.deleteIn([leftVariantIndex, 'name']);
  }

  switch (channel) {
    case ETSY: {
      variants = variants.map(updatePropertyId);
      influence = influence
        .set(ALL, false)
        .update(ATTRUBUTE.PRICE, clearInfluence)
        .update(ATTRUBUTE.QUANTITY, clearInfluence)
        .update(ATTRUBUTE.SKU, clearInfluence);

      break;
    }

    case SHOPIFY: {
      errors = variants.reduce(updateNameErrors, errors);
      break;
    }

    default: {
      break;
    }
  }

  return updateVariations({
    category,
    channel,
    inventory,
    variants,
    validateCombinations: true,
    validateOptions: true,
    variations: variations.set(INFLUENCE, influence).setIn([ERROR, VARIANTS], errors).delete(DELETED),
  });
}

export function updateVariant({
  category,
  channel,
  inventory,
  variant: sourceVariant,
  variantIndex,
  variations: sourceVariations,
}) {
  let variations = sourceVariations;
  let variant = sourceVariant;
  const variants = variations.get(VARIANTS);

  function replaceOptionsWithAvailable(options, availableOptions) {
    function mapOptions(option) {
      function findSameOption({ name }) {
        return name === option.get('value');
      }

      return option.set('valueId', availableOptions.find(findSameOption)?.id || null);
    }

    return options.map(mapOptions);
  }

  function setVariantNameError(updatedVariants) {
    return function reduce(errors, updatedVariant, index) {
      const error = getVariantNameError({
        channel,
        name: updatedVariant.get('name'),
        variants: updatedVariants,
        variantIndex: index,
      });

      return error
        ? errors.setIn([index, 'name'], error)
        : errors.deleteIn([index, 'name']);
    };
  }

  if (variant.get('property') !== variants.getIn([variantIndex, 'property'])) {
    const property = variant.get('property');
    const scale = variant.get('scale');
    const { availableScales, availableOptions } = getAvailable({ category, channel, property });

    if (variant.get('scale') === variants.getIn([variantIndex, 'scale'])) {
      if (getSize(availableScales)) {
        const analogueScale = scale && availableScales.find(findAnalogueScale({ channel, scale }));

        if (analogueScale) {
          variations = variations.deleteIn([ERROR, VARIANTS, variantIndex, 'scale']);
          variant = variant.set('scale', analogueScale.id);
        } else {
          variations = variations.setIn([ERROR, VARIANTS, variantIndex, 'scale'], true);
          variant = variant.set('scale', 0);
        }
      } else {
        variations = variations.deleteIn([ERROR, VARIANTS, variantIndex, 'scale']);
        variant = variant.delete('scale');
      }
    }

    variant = variant.set('options', replaceOptionsWithAvailable(variant.get('options'), availableOptions));

    const updatedVariants = variants.set(variantIndex, variant);
    variations = variations.setIn([ERROR, VARIANTS], updatedVariants.reduce(
      setVariantNameError(updatedVariants),
      variations.getIn([ERROR, VARIANTS])
    ));
  } else if (variant.get('name') !== variants.getIn([variantIndex, 'name'])) {
    const updatedVariants = variants.set(variantIndex, variant);

    variations = variations.setIn([ERROR, VARIANTS], updatedVariants.reduce(
      setVariantNameError(updatedVariants),
      variations.getIn([ERROR, VARIANTS])
    ));
  }

  if (variant.get('scale') !== variants.getIn([variantIndex, 'scale'])) {
    const property = variant.get('property');
    const scale = variant.get('scale');
    const { availableScales, availableOptions } = getAvailable({ category, channel, property, scale });

    variations = getSize(availableScales) > 0 && !availableScales.find(findAnalogueScale({ channel, scale }))
      ? variations.setIn([ERROR, VARIANTS, variantIndex, 'scale'], true)
      : variations.deleteIn([ERROR, VARIANTS, variantIndex, 'scale']);

    variant = variant.set('options', replaceOptionsWithAvailable(variant.get('options'), availableOptions));
  }

  return updateVariations({
    category,
    channel,
    inventory,
    variants: variants.set(variantIndex, variant),
    variations,
  });
}

export function etsyVariantHasPhotos(variant) {
  return !!variant.get('options').find(get('imageHash'));
}
